import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { CreateCustomerReportRequest, CustomerReport } from '@models/products-and-orders/customer-reports';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { addEntity, createCustomStateSetOnUrlHandler, createStateResetHandler, getAll, getOne, StateDictionary } from '../../helpers';
import { CustomerReportsService } from './customer-reports.service';

export const sortByDateAndName = (a: CustomerReport, b: CustomerReport): number => {
  const dateComparison = b.reportPeriodFrom.localeCompare(a.reportPeriodFrom);
  return dateComparison || a.partnerName.localeCompare(b.partnerName);
};

type CustomerReportsState = {
  customerReports: CustomerReport[];
  isLoading: boolean;
  customerReportCreateLoading: boolean;
  selectedReport: CustomerReport | null;
  selectedReportLoading: boolean;
  autoSubmitDayOfMonth: number | null;
  isAutoSubmitDayOfMonthLoading: boolean;
};

const initialState: CustomerReportsState = {
  customerReports: [],
  isLoading: false,
  customerReportCreateLoading: false,
  selectedReport: null,
  selectedReportLoading: false,
  autoSubmitDayOfMonth: null,
  isAutoSubmitDayOfMonthLoading: false,
};

const reportsDictionary: StateDictionary<CustomerReport> = {
  entityList: 'customerReports',
  listLoading: 'isLoading',
  selectedEntity: 'selectedReport',
  selectedEntityLoading: 'selectedReportLoading',
  entityId: 'id',
  createLoading: 'customerReportCreateLoading',
  sortFn: sortByDateAndName,
};

export const CustomerReportsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('customer-reports-store'),

  withComputed(store => ({
    selectedReportId: computed(() => store.selectedReport()?.id || null),
  })),

  withMethods((store, customerReportsService = inject(CustomerReportsService), router = inject(Router)) => {
    createStateResetHandler<CustomerReportsState>('/customer-reports')(store, initialState);
    createCustomStateSetOnUrlHandler<CustomerReportsState>('/customer-reports', { selectedReport: null })(store);

    const repositoryMethods = {
      getAll: getAll<CustomerReport>({
        store,
        service: customerReportsService,
        dictionary: reportsDictionary,
      }),

      getOne: getOne<CustomerReport>({
        store,
        service: customerReportsService,
        dictionary: reportsDictionary,
      }),

      add: addEntity<CustomerReport, CreateCustomerReportRequest>({
        store,
        service: customerReportsService,
        dictionary: reportsDictionary,
        onSuccessCallback: entity => {
          router.navigate(['customer-reports', entity.id, 'details']);
          patchState(store, { selectedReport: entity });
        },
      }),

      getAutoSubmitDayOfMonth: rxMethod<void>(
        pipe(
          tap(() => {
            patchState(store, { isAutoSubmitDayOfMonthLoading: true });
          }),
          switchMap(() =>
            customerReportsService.getAutoSubmitDayOfMonth().pipe(
              tap(response => {
                patchState(store, { autoSubmitDayOfMonth: response.autoSubmitDayOfMonth, isAutoSubmitDayOfMonthLoading: false });
              }),
              catchError(() => {
                patchState(store, { isAutoSubmitDayOfMonthLoading: false, autoSubmitDayOfMonth: null });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    const navigationActions = {
      goToReportActions: async (id: string): Promise<boolean> => {
        const result = await router.navigate(['customer-reports', id]);

        if (!result) return false;

        if (!store.selectedReportLoading()) repositoryMethods.getOne(id);

        return true;
      },

      goToListPage: (): void => {
        patchState(store, {
          selectedReport: null,
        });
        router.navigate(['customer-reports']);
      },
    };

    return {
      ...repositoryMethods,
      ...navigationActions,
    };
  }),
);

export interface CustomerReportsStoreType {
  readonly customerReports: Signal<CustomerReport[]>;
  readonly isLoading: Signal<boolean>;
  readonly customerReportCreateLoading: Signal<boolean>;
  readonly selectedReport: Signal<CustomerReport | null>;
  readonly selectedReportLoading: Signal<boolean>;
  readonly autoSubmitDayOfMonth: Signal<number | null>;
  readonly isAutoSubmitDayOfMonthLoading: Signal<boolean>;

  readonly selectedReportId: Signal<string | null>;

  readonly getAll: () => void;
  readonly getOne: (id: string) => void;
  readonly add: (request: CreateCustomerReportRequest) => void;
  readonly getAutoSubmitDayOfMonth: () => void;
  readonly goToReportActions: (id: string) => Promise<boolean>;
  readonly goToListPage: () => void;
}
